import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Form,
  Row,
  Modal,
  Table,
} from "react-bootstrap";
import { BsPersonPlusFill } from "react-icons/bs";
import {
  FaEdit,
  FaUsers,
  FaDownload,
  FaClock,
} from "react-icons/fa";
import { MdAccessTime } from "react-icons/md";
import { TbReplaceFilled } from "react-icons/tb";
import { MdDelete } from "react-icons/md";
import axios from "axios";
import AtdLayout from "../../components/AttendanceLayout.jsx";
import "./yesterday-atd.css";

export default function YesterdayAttendance() {
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [earlyCloseDocs, setEarlyCloseDocs] = useState({});
  const [overtimeDocs, setOvertimeDocs] = useState({});
  const [lateDocs, setLateDocs] = useState({});
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDocUrl, setSelectedDocUrl] = useState("");
  const [attendanceId, setAttendanceId] = useState(null);
  const [docCategory, setDocCategory] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showEarlyLeaveApprovalModal, setShowEarlyLeaveApprovalModal] = useState(false);
  const [showLateApprovalModal, setShowLateApprovalModal] = useState(false);
  const [showOvertimeApprovalModal, setShowOvertimeApprovalModal] = useState(false);
  const [approvalData, setApprovalData] = useState({});
  const [hrSignature, setHrSignature] = useState("");
  const [adminSignature, setAdminSignature] = useState("");
  const [applySingleDeduction, setApplySingleDeduction] = useState(false);
  const fileInputRef = useRef(null);

  // Break Details Modal States
  const [showBreakDetailsModal, setShowBreakDetailsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deductionMinutes, setDeductionMinutes] = useState(0);
  const [hoveredBreaks, setHoveredBreaks] = useState({});
  const [breakList, setBreakList] = useState([]);
  const [breakLoading, setBreakLoading] = useState(false);

  const fetchAttendance = () => {
    setLoading(true);
    fetch(
      `https://backend.ems.uk.hiradentallab.com/api/attendance/attendance/yesterday?search=${searchQuery}`
    )
      .then((response) => response.json())
      .then((fetchedData) => {
        const fetchedDataArr = Array.isArray(fetchedData.data) ? fetchedData.data : [];
        
        const newEarlyCloseDocs = {};
        const newOvertimeDocs = {};
        const newLateDocs = {};

        const processedData = fetchedDataArr.map((item) => {
          newEarlyCloseDocs[item._id] = item.earlyCloseDocs || null;
          newOvertimeDocs[item._id] = item.overtimeDocs || null;
          newLateDocs[item._id] = item.lateDocs || null;

          return {
            ...item,
            lateReason: item.lateReason ?? "Not provided",
            earlyCloseReason: item.earlyCloseReason ?? "Not provided",
            overtimeReason: item.overtimeReason ?? "Not provided",
            lateSingleDeduction: item.lateSingleDeduction ?? false,
            earlyCloseSingleDeduction: item.earlyCloseSingleDeduction ?? false,
            lateHrSignature: item.lateHrSignature ?? null,
            lateAdminSignature: item.lateAdminSignature ?? null,
            earlyCloseHrSignature: item.earlyCloseHrSignature ?? null,
            earlyCloseAdminSignature: item.earlyCloseAdminSignature ?? null,
            overtimeHrSignature: item.overtimeHrSignature ?? null,
            overtimeAdminSignature: item.overtimeAdminSignature ?? null,
            scheduledBreakMinutes: item.scheduledBreakMinutes ?? 0,
            paidBreakMinutes: item.paidBreakMinutes ?? item.totalPaidBreakMinutes ?? 0,
            breakDeductionMinutes: item.breakDeductionMinutes ?? item.totalUnpaidBreakMinutes ?? 0,
            totalPaidBreakMinutes: item.totalPaidBreakMinutes ?? 0,
            totalUnpaidBreakMinutes: item.totalUnpaidBreakMinutes ?? 0,
            totalExcessUnpaidMinutes: item.totalExcessUnpaidMinutes ?? 0,
            autoBreakApplied: item.autoBreakApplied ?? false,
            autoBreakDeduction: item.autoBreakDeduction ?? null,
          };
        });

        setData(processedData);
        setFilteredData(processedData);
        setEarlyCloseDocs(newEarlyCloseDocs);
        setOvertimeDocs(newOvertimeDocs);
        setLateDocs(newLateDocs);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching attendance data:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAttendance();
  }, [searchQuery]);

  const handleShowModal = (data) => {
    setModalData(data);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalData({});
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAttendance();
  };

  const handleViewDocument = (docUrl, item, category) => {
    if (!docUrl || !item || !item._id) {
      console.error("Invalid document or item:", { docUrl, item });
      alert("Something went wrong. Document or attendance data missing.");
      return;
    }
    let formattedUrl = docUrl.startsWith("http")
      ? docUrl
      : `https://backend.ems.uk.hiradentallab.com${docUrl.startsWith("/uploads") ? docUrl : `/uploads/${docUrl}`}`;
    setSelectedDocUrl(formattedUrl);
    setDocCategory(category);
    setShowViewModal(true);
    setAttendanceId(item._id);
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(selectedDocUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename = selectedDocUrl.split("/").pop() || "document";
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Download failed. Please try again.");
    }
  };

  const handleFileReplace = async (e) => {
    const file = e.target.files[0];
    if (!file || !attendanceId || !docCategory) {
      alert("No file selected, attendance ID missing, or document category not specified.");
      return;
    }
    const formData = new FormData();
    formData.append("document", file);
    formData.append("uploadCategory", docCategory);
    try {
      const response = await fetch(
        `https://backend.ems.uk.hiradentallab.com/api/attendance/update-document/${attendanceId}`,
        { method: "POST", body: formData }
      );
      const data = await response.json();
      if (response.ok) {
        alert("Document replaced successfully!");
        setShowViewModal(false);
        fetchAttendance();
      } else {
        alert(data.message || "Failed to replace document");
      }
    } catch (error) {
      console.error("Error replacing file:", error);
      alert("Error replacing file.");
    }
  };

  const resetApprovalData = () => {
    setApprovalData({});
    setHrSignature("");
    setAdminSignature("");
    setApplySingleDeduction(false);
  };

  const handleOpenLateApprovalForm = (item) => {
    resetApprovalData();
    const currentRole = localStorage.getItem("role");
    const hrHasApproved = !!item.lateHrSignature;
    const adminHasApproved = !!item.lateAdminSignature;
    const canEditHRSignature = currentRole === "HR" && !hrHasApproved;
    const canEditAdminSignature = currentRole === "admin" && !adminHasApproved;

    setApprovalData({
      userName: item.userName,
      userId: item.userId,
      date: new Date(item.checkIn).toLocaleDateString("en-GB", { timeZone: "Europe/London" }),
      department: item.department || "Not specified",
      type: "Late Arrival",
      minutes: item.lateMinutes,
      reason: item.lateReason || "Not provided",
      attendanceId: item._id,
      singleDeduction: item.lateSingleDeduction || false,
      hrHasApproved,
      adminHasApproved,
      canEditHRSignature,
      canEditAdminSignature,
      currentRole,
    });
    setHrSignature(item.lateHrSignature || "");
    setAdminSignature(item.lateAdminSignature || "");
    setApplySingleDeduction(item.lateSingleDeduction || false);
    setShowLateApprovalModal(true);
  };

  const handleOpenEarlyLeaveApprovalForm = (item) => {
    resetApprovalData();
    const currentRole = localStorage.getItem("role");
    const hrHasApproved = !!item.earlyCloseHrSignature;
    const adminHasApproved = !!item.earlyCloseAdminSignature;
    const canEditHRSignature = currentRole === "HR" && !hrHasApproved;
    const canEditAdminSignature = currentRole === "admin" && !adminHasApproved;

    setApprovalData({
      userName: item.userName,
      userId: item.userId,
      date: new Date(item.checkIn).toLocaleDateString("en-GB", { timeZone: "Europe/London" }),
      department: item.department || "Not specified",
      type: "Early Leave",
      minutes: item.earlyCloseMinutes,
      reason: item.earlyCloseReason || "Not provided",
      attendanceId: item._id,
      singleDeduction: item.earlyCloseSingleDeduction || false,
      hrHasApproved,
      adminHasApproved,
      canEditHRSignature,
      canEditAdminSignature,
      currentRole,
    });
    setHrSignature(item.earlyCloseHrSignature || "");
    setAdminSignature(item.earlyCloseAdminSignature || "");
    setApplySingleDeduction(item.earlyCloseSingleDeduction || false);
    setShowEarlyLeaveApprovalModal(true);
  };

  const handleOpenOvertimeApprovalForm = (item) => {
    resetApprovalData();
    const currentRole = localStorage.getItem("role");
    const hrHasApproved = !!item.overtimeHrSignature;
    const adminHasApproved = !!item.overtimeAdminSignature;
    const canEditHRSignature = currentRole === "HR" && !hrHasApproved;
    const canEditAdminSignature = currentRole === "admin" && !adminHasApproved;

    setApprovalData({
      userName: item.userName,
      userId: item.userId,
      date: new Date(item.checkIn).toLocaleDateString("en-GB", { timeZone: "Europe/London" }),
      department: item.department || "Not specified",
      type: "Overtime",
      minutes: item.extraMinutes,
      reason: item.overtimeReason || "Not provided",
      attendanceId: item._id,
      hrHasApproved,
      adminHasApproved,
      canEditHRSignature,
      canEditAdminSignature,
      currentRole,
    });
    setHrSignature(item.overtimeHrSignature || "");
    setAdminSignature(item.overtimeAdminSignature || "");
    setApplySingleDeduction(false);
    setShowOvertimeApprovalModal(true);
  };

  const handleApprove = async (type) => {
    const role = localStorage.getItem("role");
    if (!role || (role !== "HR" && role !== "admin")) {
      alert("You do not have permission to approve this request.");
      return;
    }
    if (role === "HR" && !hrSignature) {
      alert("Please provide an HR signature before approving.");
      return;
    }
    if (role === "admin" && !adminSignature) {
      alert("Please provide an Admin signature before approving.");
      return;
    }

    try {
      const updateData = {
        type,
        approve: role === "admin" ? true : undefined,
        semiApproved: role === "HR" ? true : undefined,
        deduct: type !== "overtime" && role === "admin" ? !applySingleDeduction : undefined,
        singleDeduction: type !== "overtime" && role === "admin" ? applySingleDeduction : undefined,
        hrSignature: role === "HR" ? hrSignature : undefined,
        adminSignature: role === "admin" ? adminSignature : undefined,
      };

      const response = await axios.put(
        `https://backend.ems.uk.hiradentallab.com/api/attendance/${approvalData.attendanceId}/deduct`,
        updateData,
        { headers: { "x-user-role": role } }
      );

      if (response.status === 200) {
        alert(`Attendance ${type} ${role === "admin" ? "approved" : "semi-approved"} successfully!`);
        fetchAttendance();
        if (type === "late") setShowLateApprovalModal(false);
        if (type === "earlyClose") setShowEarlyLeaveApprovalModal(false);
        if (type === "overtime") setShowOvertimeApprovalModal(false);
        resetApprovalData();
      } else {
        alert("Failed to approve attendance record.");
      }
    } catch (error) {
      console.error("Error approving attendance:", error);
      alert("Error approving attendance record.");
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      const response = await axios.post(
        "https://backend.ems.uk.hiradentallab.com/api/attendance/attendance/delete",
        { attendanceId: itemToDelete._id }
      );
      if (response.data.success) {
        alert("Attendance record deleted successfully!");
        fetchAttendance();
      } else {
        alert(response.data.message || "Failed to delete attendance record");
      }
    } catch (error) {
      console.error("Error deleting attendance:", error);
      alert("Error deleting attendance record.");
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const handleOpenBreakDetailsModal = async (item) => {
    setSelectedItem(item);
    setDeductionMinutes(item.breakDeductionMinutes || 0);
    setShowBreakDetailsModal(true);
    setBreakList([]);
    setBreakLoading(true);

    try {
      const date = new Date(item.checkIn).toLocaleDateString("en-CA", { timeZone: "Europe/London" });
      const url = `https://backend.ems.uk.hiradentallab.com/api/attendance/breaks?userId=${item.userId}&date=${date}`;
      const res = await fetch(url);
      const json = await res.json();
      if (json.success) {
        setBreakList(json.data.breaks || json.data.paidBreaks || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setBreakLoading(false);
    }
  };

  const handleDeductBreak = async () => {
    if (deductionMinutes < 0) {
      alert("Please enter a valid number of minutes (0 or greater).");
      return;
    }
    try {
      const response = await axios.post(
        `https://backend.ems.uk.hiradentallab.com/api/attendance/deduct-break/${selectedItem._id}`,
        { minutes: deductionMinutes }
      );
      if (response.status === 200) {
        alert("Break deduction updated successfully!");
        setShowBreakDetailsModal(false);
        fetchAttendance();
      } else {
        alert("Failed to update break deduction.");
      }
    } catch (error) {
      console.error("Error updating break deduction:", error);
      alert("An error occurred while updating break deduction.");
    }
  };

  const renderBreakButton = (item) => {
    const isHovered = hoveredBreaks[item._id] || false;
    return (
      <Button
        variant="primary"
        size="sm"
        style={{
          background: isHovered
            ? "linear-gradient(135deg, #0069d9, #007bff)"
            : "linear-gradient(135deg, #007bff, #0056b3)",
          border: "none",
          color: "#fff",
          fontWeight: 600,
          padding: "6px 16px",
          borderRadius: "20px",
          boxShadow: isHovered
            ? "0 4px 12px rgba(0,123,255,0.4)"
            : "0 2px 6px rgba(0,123,255,0.2)",
          transition: "all 0.3s ease",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
        }}
        onMouseEnter={() => setHoveredBreaks((prev) => ({ ...prev, [item._id]: true }))}
        onMouseLeave={() => setHoveredBreaks((prev) => ({ ...prev, [item._id]: false }))}
        onClick={() => handleOpenBreakDetailsModal(item)}
      >
        <FaClock style={{ fontSize: "0.9rem" }} /> Details
      </Button>
    );
  };

  const totalPages = Math.ceil(data.length / rowsPerPage);

  const pageRange = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(startPage + 4, totalPages);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <AtdLayout>
      <div>
        <h1 className="yesterday-attendance-title">
          <FaUsers className="settings-icon" /> Employees - Yesterday
        </h1>
        <hr />
        <div className="yesterday-attendance-content">
          <Row>
            <Col xs="auto">
              <Link to="/add-atd">
                <Button variant="primary" style={{ marginBottom: "10px" }}>
                  <BsPersonPlusFill /> Add Attendance
                </Button>
              </Link>
            </Col>
          </Row>

          <div className="yesterday-attendance-actions">
            <Row>
              <Col xs={12} md={6} className="d-flex align-items-center">
                <Dropdown as={ButtonGroup}>
                  <Button variant="primary">Show Entries</Button>
                  <Dropdown.Toggle split variant="primary" id="dropdown-split-basic" />
                  <Dropdown.Menu>
                    <Dropdown.Item as="button">10</Dropdown.Item>
                    <Dropdown.Item as="button">25</Dropdown.Item>
                    <Dropdown.Item as="button">50</Dropdown.Item>
                    <Dropdown.Item as="button">100</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col xs={12} md={6} className="d-flex justify-content-end">
                <div className="search-container">
                  <Form className="d-flex" onSubmit={handleSearchSubmit}>
                    <Form.Control
                      type="search"
                      placeholder="Search"
                      aria-label="Search"
                      className="hr-info-search"
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                  </Form>
                </div>
              </Col>
            </Row>
          </div>

          <div className="table-responsive">
            {loading ? (
              <p>Loading...</p>
            ) : (
              <Table striped hover className="table-style">
                <thead className="yesterday-attendance-table-header" style={{ backgroundColor: "#007bff", color: "#fff" }}>
                  <tr className="table-header">
                    <th>SR</th>
                    <th>UserId</th>
                    <th>Employee Name</th>
                    <th>CheckIn</th>
                    <th>CheckOut</th>
                    <th>Worked Hours</th>
                    <th>Late Absent Minutes</th>
                    <th>Early Absent Minutes</th>
                    <th>Overtime</th>
                    <th>Break Details</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData
                    .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                    .map((item, index) => (
                      <tr key={item._id || index}>
                        <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                        <td>{item.userId}</td>
                        <td>{item.userName}</td>
                        <td>
                          {new Date(item.checkIn).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                            timeZone: "Europe/London",
                          })}
                        </td>
                        <td>
                          {item.checkOut
                            ? new Date(item.checkOut).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                                timeZone: "Europe/London",
                              })
                            : "At Work"}
                        </td>
                        <td>{item.workedHours}</td>
                        <td>{item.lateMinutes || "0"}</td>
                        <td>{item.earlyCloseMinutes || "0"}</td>
                        <td>{item.extraMinutes || "0"}</td>
                        <td>{renderBreakButton(item)}</td>
                        <td>
                          <div>
                            <Link to={`/edit-atd/${item._id}`}>
                              <Button variant="warning" size="sm" style={{ marginRight: "5px" }}>
                                <FaEdit />
                              </Button>
                            </Link>
                            <Button variant="success" size="sm" onClick={() => handleShowModal(item)} style={{ marginRight: "5px" }}>
                              <MdAccessTime />
                            </Button>
                            <Button size="sm" variant="danger" onClick={() => handleDeleteClick(item)}>
                              <MdDelete />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
                <tfoot className="provident-fund-footer">
                  <tr className="provident-fund-table-footer" style={{ backgroundColor: "#007bff", color: "#fff" }}>
                    <td>Total</td>
                    <td></td>
                    <td>{filteredData.length}</td>
                    <td colSpan="8"></td>
                  </tr>
                </tfoot>
              </Table>
            )}
          </div>

          <div className="pagination-row">
            <div className="entries-info">
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, data.length)} of {data.length} entries
            </div>
            <nav>
              <ul className="pagination no-gap">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <a href="/#" className="page-link" onClick={(e) => { e.preventDefault(); handlePageChange(currentPage - 1); }}>
                    Previous
                  </a>
                </li>
                {pageRange().map((pageNumber) => (
                  <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? "active" : ""}`}>
                    <a href="/#" className="page-link" onClick={(e) => { e.preventDefault(); handlePageChange(pageNumber); }}>
                      {pageNumber}
                    </a>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <a href="/#" className="page-link" onClick={(e) => { e.preventDefault(); handlePageChange(currentPage + 1); }}>
                    Next
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Attendance Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Attendance Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>UserId:</strong> {modalData.userId}</p>
          <p><strong>Name:</strong> {modalData.userName}</p>
          <p><strong>CheckIn:</strong> {modalData.checkIn ? new Date(modalData.checkIn).toLocaleString("en-GB", { timeZone: "Europe/London" }) : "N/A"}</p>
          <p><strong>CheckOut:</strong> {modalData.checkOut ? new Date(modalData.checkOut).toLocaleString("en-GB", { timeZone: "Europe/London" }) : "N/A"}</p>
          <p><strong>Worked Hours:</strong> {modalData.workedHours}</p>
          <p><strong>Late Reason:</strong> {modalData.lateReason || "Not provided"}</p>
          <p><strong>Early Close Reason:</strong> {modalData.earlyCloseReason || "Not provided"}</p>
          <p><strong>Overtime Reason:</strong> {modalData.overtimeReason || "Not provided"}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Document View Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <span>View Document</span>
            <div style={{ display: "flex", gap: "10px" }}>
              {selectedDocUrl && (
                <Button variant="outline-success" onClick={handleDownload}>
                  <FaDownload />
                </Button>
              )}
              <Button variant="outline-warning" onClick={() => fileInputRef.current?.click()}>
                <TbReplaceFilled />
              </Button>
              <input ref={fileInputRef} type="file" accept=".jpg,.png,.pdf" style={{ display: "none" }} onChange={handleFileReplace} />
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: "center" }}>
          {selectedDocUrl ? (
            selectedDocUrl.endsWith(".pdf") ? (
              <iframe src={selectedDocUrl} width="100%" height="500px" style={{ border: "none" }} title="PDF Document Viewer" />
            ) : (
              <img src={selectedDocUrl} alt="Uploaded Document" style={{ maxWidth: "100%", height: "auto", borderRadius: "8px" }} />
            )
          ) : (
            <p style={{ color: "red", fontWeight: "bold" }}>No document available</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════
          BREAK DETAILS MODAL — UPDATED WITH AUTO BREAK DEDUCTION
          ═══════════════════════════════════════════════════════════════ */}
      <Modal show={showBreakDetailsModal} onHide={() => setShowBreakDetailsModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ backgroundColor: "#007bff", color: "#fff" }}>
          <Modal.Title style={{ color: "#fff" }}>
            <FaClock className="me-2" />
            Break Details – {selectedItem?.userName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f8f9fa", borderRadius: "0 0 10px 10px" }}>
          {breakLoading ? (
            <p className="text-center">Loading breaks...</p>
          ) : (
            <div style={{ background: "#fff", borderRadius: "8px", padding: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
              
              {/* ── AUTO BREAK DEDUCTION ALERT ── */}
              {selectedItem?.autoBreakApplied && selectedItem?.autoBreakDeduction && (
                <div
                  className="alert mb-3"
                  style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#fff",
                    borderRadius: "10px",
                    border: "none",
                    padding: "14px 18px",
                    fontSize: "0.9rem",
                  }}
                >
                  <div className="d-flex align-items-start gap-2">
                    <div
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "50%",
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <FaClock style={{ fontSize: "0.85rem" }} />
                    </div>
                    <div>
                      <strong style={{ fontSize: "1rem" }}>Auto Break Deducted</strong>
                      <p className="mb-1 mt-1" style={{ opacity: 0.9 }}>
                        No manual break recorded. System auto-deducted{" "}
                        <strong>{selectedItem.autoBreakDeduction.totalAutoBreakMinutes} min</strong> based on schedule.
                      </p>
                      <div className="d-flex gap-2 flex-wrap mt-2">
                        {selectedItem.autoBreakDeduction.paidMinutes > 0 && (
                          <span className="badge" style={{ background: "rgba(255,255,255,0.25)", color: "#fff", fontSize: "0.8rem", padding: "4px 10px", borderRadius: "12px" }}>
                            Paid: {selectedItem.autoBreakDeduction.paidMinutes}m
                          </span>
                        )}
                        {selectedItem.autoBreakDeduction.unpaidMinutes > 0 && (
                          <span className="badge" style={{ background: "rgba(255,255,255,0.25)", color: "#fff", fontSize: "0.8rem", padding: "4px 10px", borderRadius: "12px" }}>
                            Unpaid: {selectedItem.autoBreakDeduction.unpaidMinutes}m
                          </span>
                        )}
                        {selectedItem.autoBreakDeduction.excessUnpaidMinutes > 0 && (
                          <span className="badge" style={{ background: "rgba(255,255,255,0.25)", color: "#fff", fontSize: "0.8rem", padding: "4px 10px", borderRadius: "12px" }}>
                            Excess: {selectedItem.autoBreakDeduction.excessUnpaidMinutes}m
                          </span>
                        )}
                        {selectedItem.autoBreakDeduction.isSixHourSpecial && (
                          <span className="badge" style={{ background: "rgba(255,193,7,0.3)", color: "#ffc107", fontSize: "0.8rem", padding: "4px 10px", borderRadius: "12px" }}>
                            6-Hour Shift Special
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── MANUAL BREAK TABLE ── */}
              {breakList.length === 0 ? (
                !selectedItem?.autoBreakApplied ? (
                  <p className="text-center text-muted">No break records found.</p>
                ) : null
              ) : (
                <table className="table table-hover mb-0" style={{ fontSize: "0.9rem" }}>
                  <thead style={{ backgroundColor: "#e9ecef" }}>
                    <tr>
                      <th>#</th>
                      <th>Break In</th>
                      <th>Break Out</th>
                      <th>Total Duration</th>
                      <th>Breakdown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakList.map((b, idx) => (
                      <tr key={b._id || idx}>
                        <td>{idx + 1}</td>
                        <td>
                          {b.breakIn ? new Date(b.breakIn).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                            timeZone: "Europe/London",
                          }) : "—"}
                        </td>
                        <td>
                          {b.breakOut ? new Date(b.breakOut).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                            timeZone: "Europe/London",
                          }) : "—"}
                        </td>
                        <td>
                          <span className="fw-bold">{b.durationMinutes} min</span>
                        </td>
                        <td>
                          {b.paidMinutes > 0 && (
                            <span className="badge bg-success me-1">Paid: {b.paidMinutes}m</span>
                          )}
                          {b.unpaidMinutes > 0 && (
                            <span className="badge bg-warning text-dark me-1">Unpaid: {b.unpaidMinutes}m</span>
                          )}
                          {b.excessUnpaidMinutes > 0 && (
                            <span className="badge bg-danger">Excess: {b.excessUnpaidMinutes}m</span>
                          )}
                          {b.paidMinutes === 0 && b.unpaidMinutes === 0 && b.excessUnpaidMinutes === 0 && (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* ── SUMMARY FOOTER ── */}
              <div className="mt-3 p-2" style={{ background: "#f1f3f4", borderRadius: "6px", fontSize: "0.85rem" }}>
                <div className="d-flex justify-content-between flex-wrap gap-2">
                  <span><strong>Total Break:</strong> {selectedItem?.totalBreakMinutes || 0} min</span>
                  <span><strong>Paid:</strong> {selectedItem?.totalPaidBreakMinutes || 0} min</span>
                  <span><strong>Unpaid:</strong> {selectedItem?.totalUnpaidBreakMinutes || 0} min</span>
                  <span><strong>Excess:</strong> {selectedItem?.totalExcessUnpaidMinutes || 0} min</span>
                  <span><strong>Deducted:</strong> {selectedItem?.breakDeductionMinutes || 0} min</span>
                </div>
              </div>

              <hr />

              {/* ── MANUAL DEDUCTION ADJUST ── */}
              <div style={{ background: "#fff", borderRadius: "8px", padding: "16px" }}>
                <Form.Group>
                  <Form.Label><strong>Adjust Unpaid Break Deduction (minutes):</strong></Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={deductionMinutes}
                    onChange={(e) => setDeductionMinutes(parseInt(e.target.value) || 0)}
                  />
                  <Form.Text className="text-muted">
                    Override the automatic break deduction if needed.
                  </Form.Text>
                </Form.Group>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBreakDetailsModal(false)}>Close</Button>
          <Button variant="primary" onClick={handleDeductBreak}>Update Deduction</Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => { setShowDeleteModal(false); setItemToDelete(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the attendance record for {itemToDelete?.userName}?
          <br />
          <strong>This action cannot be undone.</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDeleteModal(false); setItemToDelete(null); }}>Cancel</Button>
          <Button variant="danger" onClick={handleConfirmDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>

      {/* Late Approval Modal */}
      <Modal show={showLateApprovalModal} onHide={() => { setShowLateApprovalModal(false); resetApprovalData(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Late Arrival Approval</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>Employee:</strong> {approvalData.userName}</p>
          <p><strong>Date:</strong> {approvalData.date}</p>
          <p><strong>Department:</strong> {approvalData.department}</p>
          <p><strong>Late Minutes:</strong> {approvalData.minutes}</p>
          <p><strong>Reason:</strong> {approvalData.reason}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowLateApprovalModal(false); resetApprovalData(); }}>Close</Button>
          <Button variant="primary" onClick={() => handleApprove("late")}>Approve</Button>
        </Modal.Footer>
      </Modal>

      {/* Early Leave Approval Modal */}
      <Modal show={showEarlyLeaveApprovalModal} onHide={() => { setShowEarlyLeaveApprovalModal(false); resetApprovalData(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Early Leave Approval</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>Employee:</strong> {approvalData.userName}</p>
          <p><strong>Date:</strong> {approvalData.date}</p>
          <p><strong>Department:</strong> {approvalData.department}</p>
          <p><strong>Early Minutes:</strong> {approvalData.minutes}</p>
          <p><strong>Reason:</strong> {approvalData.reason}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowEarlyLeaveApprovalModal(false); resetApprovalData(); }}>Close</Button>
          <Button variant="primary" onClick={() => handleApprove("earlyClose")}>Approve</Button>
        </Modal.Footer>
      </Modal>

      {/* Overtime Approval Modal */}
      <Modal show={showOvertimeApprovalModal} onHide={() => { setShowOvertimeApprovalModal(false); resetApprovalData(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Overtime Approval</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>Employee:</strong> {approvalData.userName}</p>
          <p><strong>Date:</strong> {approvalData.date}</p>
          <p><strong>Department:</strong> {approvalData.department}</p>
          <p><strong>Overtime Minutes:</strong> {approvalData.minutes}</p>
          <p><strong>Reason:</strong> {approvalData.reason}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowOvertimeApprovalModal(false); resetApprovalData(); }}>Close</Button>
          <Button variant="primary" onClick={() => handleApprove("overtime")}>Approve</Button>
        </Modal.Footer>
      </Modal>
    </AtdLayout>
  );
}